import { z } from 'zod';

import type { ConnectionTestResult } from '@littlebird/axon-mcp';

export const APPLE_NOTES_TEMPLATE_ID = 'apple-notes';

const doctorReportSchema = z.object({
  checks: z.array(
    z.object({
      name: z.string(),
      status: z.enum(['ok', 'warn', 'fail']),
      detail: z.string(),
    })
  ),
});

export interface NotesDoctorReport {
  /** As the server lists them, comma-separated; empty when Notes has none or they couldn't be read */
  accountNames: string;
  controlAllowed: boolean;
}

// apple-notes-mcp's `doctor` writes the Accounts detail as `3 account(s): Google, iCloud`
const ACCOUNTS_DETAIL_RE = /^\d+ account\(s\): (.+)$/;
// Either check fails when macOS refuses to let Littlebird control Notes
const CONTROL_CHECKS = ['Notes.app: notes_app', 'Notes.app: permissions'];

/** Reads the `doctor` report the connection test's probe returned */
export function parseNotesDoctorReport(
  result: ConnectionTestResult | null
): NotesDoctorReport | null {
  const report = doctorReportSchema.safeParse(result?.probeStructuredContent);
  if (!report.success) return null;

  const { checks } = report.data;
  const accounts = checks.find((check) => check.name === 'Accounts');
  return {
    accountNames:
      accounts?.status === 'ok'
        ? (ACCOUNTS_DETAIL_RE.exec(accounts.detail)?.[1] ?? '')
        : '',
    controlAllowed: !checks.some(
      (check) => CONTROL_CHECKS.includes(check.name) && check.status === 'fail'
    ),
  };
}

const notesStatsSchema = z.object({
  accounts: z.array(
    z.object({
      name: z.string(),
      folders: z.array(z.object({ name: z.string(), noteCount: z.number() })),
    })
  ),
});

export interface NotesFolder {
  name: string;
  noteCount: number;
  accountName: string;
}

/** Reads the folders out of `get-notes-stats`'s `structuredContent` */
export function parseNotesFolders(stats: unknown): NotesFolder[] | null {
  const parsed = notesStatsSchema.safeParse(stats);
  if (!parsed.success) return null;
  return parsed.data.accounts.flatMap((account) =>
    account.folders.map((folder) => ({ ...folder, accountName: account.name }))
  );
}
